import { useState, useEffect } from 'react'
import { api } from '../lib/api'
import { MarketBadge } from '../components/UI'
import toast from 'react-hot-toast'


function useProducts() {
  const [products, setProducts] = useState([])
  useEffect(() => { api.getProducts().then(setProducts).catch(() => {}) }, [])
  return products
}
function useActiveMarkets() {
  const [markets, setMarkets] = useState([])
  useEffect(() => {
    api.getSettings().then(s => {
      setMarkets(s.active_markets || ['SE','NL-BE','UK','US','AU','NO','GE','FR','DK','FI','CA'])
    }).catch(() => setMarkets(['SE','NL-BE','UK','US','AU','NO','GE','FR','DK','FI','CA']))
  }, [])
  return markets
}

export default function PriceCalculator() {
  const products = useProducts()
  const activeMarkets = useActiveMarkets()
  const [priceEur, setPriceEur] = useState('')
  const [productId, setProductId] = useState('')
  const [competitorInfo, setCompetitorInfo] = useState('')
  const [rates, setRates] = useState(null)
  const [marketCurrency, setMarketCurrency] = useState({})
  const [loadingRates, setLoadingRates] = useState(true)
  const [strategy, setStrategy] = useState(null)
  const [thinking, setThinking] = useState(false)

  useEffect(() => {
    api.getRates()
      .then(d => { setRates(d.rates); setMarketCurrency(d.marketCurrency || {}) })
      .catch(() => toast.error('Could not load live rates'))
      .finally(() => setLoadingRates(false))
  }, [])

  const currencyFor = (m) => marketCurrency[m] || 'EUR'
  const convert = (eur, m) => {
    const cur = currencyFor(m)
    if (cur === 'EUR') return parseFloat(eur)
    const rate = rates?.[cur]
    return rate ? parseFloat(eur) * rate : null
  }

  function pickProduct(pid) {
    setProductId(pid)
    const p = products.find(x => String(x.product_id) === String(pid))
    if (p) {
      if (p.selling_price_eur) setPriceEur(String(p.selling_price_eur))
      const ci = []
      if (p.competitor_price) ci.push('Competitor price: ' + p.competitor_price)
      if (p.competitor_product_url) ci.push('Competitor URL: ' + p.competitor_product_url)
      if (ci.length) setCompetitorInfo(ci.join('\n'))
    }
  }

  const marketRows = activeMarkets.map(m => ({
    market: m, currency: currencyFor(m),
    converted: priceEur ? convert(priceEur, m) : null
  }))

  async function suggest() {
    if (!priceEur || isNaN(parseFloat(priceEur))) { toast.error('Enter a EUR price first'); return }
    setThinking(true); setStrategy(null)
    try {
      const p = products.find(x => String(x.product_id) === String(productId))
      const d = await api.pricingStrategy({
        basePriceEur: parseFloat(priceEur),
        productName: p?.product_name || '',
        competitorInfo,
        marketPrices: marketRows.map(r => ({ market: r.market, currency: r.currency, converted: r.converted != null ? Math.round(r.converted) : null }))
      })
      setStrategy(d)
    } catch (err) { toast.error(err.message) }
    setThinking(false)
  }

  const fmt = (n, cur) => n == null ? '—' : new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(n) + ' ' + cur

  return (
    <div style={{flex:1,overflow:'auto',padding:'24px 28px',maxWidth:1000}}>
      <div style={{marginBottom:20}}>
        <div style={{fontSize:20,fontWeight:700,color:'var(--text)'}}>🧮 Price Calculator</div>
        <div style={{fontSize:13,color:'var(--text3)',marginTop:4}}>Enter a EUR price (or pick a product), see it in every market at live rates, and get AI pricing strategies.</div>
      </div>

      <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:12,padding:18,marginBottom:18}}>
        <div style={{display:'flex',gap:16,flexWrap:'wrap',alignItems:'flex-end'}}>
          <div>
            <label style={{fontSize:10,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.6px',fontFamily:'DM Mono,monospace',display:'block',marginBottom:5}}>Price (EUR)</label>
            <input type="number" step="0.01" value={priceEur} onChange={e=>setPriceEur(e.target.value)} placeholder="34.99"
              style={{background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:8,color:'var(--text)',padding:'9px 12px',fontSize:15,fontWeight:700,width:140,outline:'none',fontFamily:'DM Mono,monospace'}} />
          </div>
          <div style={{fontSize:12,color:'var(--text3)',paddingBottom:10}}>or</div>
          <div>
            <label style={{fontSize:10,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.6px',fontFamily:'DM Mono,monospace',display:'block',marginBottom:5}}>Pick a Product</label>
            <select value={productId} onChange={e=>pickProduct(e.target.value)}
              style={{background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:8,color:'var(--text)',padding:'9px 12px',fontSize:13,minWidth:240,outline:'none'}}>
              <option value="">— none —</option>
              {products.map(p => <option key={p.product_id} value={p.product_id}>{p.product_id} — {p.product_name}</option>)}
            </select>
          </div>
        </div>
        <div style={{marginTop:14}}>
          <label style={{fontSize:10,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.6px',fontFamily:'DM Mono,monospace',display:'block',marginBottom:5}}>Competitor Info (URL or price — helps the AI)</label>
          <textarea value={competitorInfo} onChange={e=>setCompetitorInfo(e.target.value)} placeholder="e.g. try-zynea.com sells at 34.90 EUR with free shipping, bundle 3 for 69"
            style={{width:'100%',minHeight:60,background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:8,color:'var(--text)',padding:'9px 12px',fontSize:13,outline:'none',fontFamily:'DM Sans,sans-serif'}} />
        </div>
      </div>

      <div style={{fontSize:11,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'1px',fontFamily:'DM Mono,monospace',marginBottom:10}}>
        Converted Price {loadingRates ? '(loading rates…)' : '(live rates)'}
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',gap:10,marginBottom:20}}>
        {marketRows.map(r => (
          <div key={r.market} style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:10,padding:'12px 14px'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6}}>
              <MarketBadge market={r.market} />
              <span style={{fontSize:10,color:'var(--text3)',fontFamily:'DM Mono,monospace'}}>{r.currency}</span>
            </div>
            <div style={{fontSize:18,fontWeight:700,color:'var(--text)',fontFamily:'DM Mono,monospace'}}>
              {r.converted != null ? fmt(Math.round(r.converted), r.currency) : '—'}
            </div>
          </div>
        ))}
      </div>

      <button className="btn btn-primary" onClick={suggest} disabled={thinking || !priceEur}>
        {thinking ? '⏳ Analyzing…' : '✨ Suggest Pricing Strategies'}
      </button>

      {strategy?.markets && (
        <div style={{marginTop:24,display:'flex',flexDirection:'column',gap:16}}>
          {strategy.markets.map(m => (
            <div key={m.market} style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:12,padding:16}}>
              <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:12}}>
                <MarketBadge market={m.market} />
                <span style={{fontSize:11,color:'var(--text3)',fontFamily:'DM Mono,monospace'}}>{m.currency}</span>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:10}}>
                {['aggressive','balanced','premium'].map(tier => {
                  const st = m.strategies?.[tier]
                  if (!st) return null
                  const isRec = tier === 'balanced'
                  const tierColor = tier==='aggressive'?'#F59E0B':tier==='premium'?'var(--accent)':'var(--accent3)'
                  return (
                    <div key={tier} style={{background:'var(--bg3)',border:'1px solid '+(isRec?'var(--accent3)':'var(--border2)'),borderRadius:10,padding:'12px 14px'}}>
                      <div style={{fontSize:10,fontWeight:700,color:tierColor,textTransform:'uppercase',letterSpacing:'0.6px',fontFamily:'DM Mono,monospace',marginBottom:8}}>
                        {tier}{isRec?' ★':''}
                      </div>
                      <div style={{display:'flex',flexDirection:'column',gap:4,fontFamily:'DM Mono,monospace',fontSize:13}}>
                        <div style={{display:'flex',justifyContent:'space-between'}}><span style={{color:'var(--text3)'}}>1x</span><strong style={{color:'var(--text)'}}>{fmt(st.x1, m.currency)}</strong></div>
                        <div style={{display:'flex',justifyContent:'space-between'}}><span style={{color:'var(--text3)'}}>2x</span><strong style={{color:'var(--text)'}}>{fmt(st.x2, m.currency)}</strong></div>
                        <div style={{display:'flex',justifyContent:'space-between'}}><span style={{color:'var(--text3)'}}>3x</span><strong style={{color:'var(--text)'}}>{fmt(st.x3, m.currency)}</strong></div>
                      </div>
                    </div>
                  )
                })}
              </div>
              {m.reasoning && <div style={{fontSize:12,color:'var(--text2)',marginTop:10,lineHeight:1.5,fontStyle:'italic'}}>{m.reasoning}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
